'use client';
import React, { useEffect, useState, useRef } from 'react';
import { shouldShowSignUp } from '@/lib/utils';
import { ContainerScroll } from '@/components/ui/container-scroll-animation';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { HiArrowRight } from 'react-icons/hi2';
import { AnimatedGradientText } from '@/components/ui/animated-gradient-text';
import { useTranslation } from 'react-i18next';
import { useMobile } from '@/hooks/use-mobile';

export function HeroScrollDemo() {
  const { t, i18n } = useTranslation('common');
  const [dashboardImage, setDashboardImage] = useState('/screens/english-screen.png');
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobile();
  const showSignUp = shouldShowSignUp();

  useEffect(() => {
    const currentLanguage = i18n.language;

    if (currentLanguage === 'de') {
      setDashboardImage(
        isMobile ? '/screens/german-mobile-screen.png' : '/screens/german-screen.png'
      );
    } else {
      // Use English screenshots for all other languages including Spanish
      setDashboardImage(
        isMobile ? '/screens/english-mobile-screen.png' : '/screens/english-screen.png'
      );
    }
  }, [i18n.language, isMobile]);

  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const language = customEvent.detail;

      if (language === 'de') {
        setDashboardImage(
          isMobile ? '/screens/german-mobile-screen.png' : '/screens/german-screen.png'
        );
      } else {
        // Use English screenshots for all other languages including Spanish
        setDashboardImage(
          isMobile ? '/screens/english-mobile-screen.png' : '/screens/english-screen.png'
        );
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [isMobile]);

  return (
    <div
      className="flex flex-col overflow-hidden relative"
      ref={containerRef}
      style={{ position: 'relative' }}
    >
      <ContainerScroll
        titleComponent={
          <>
            <h1 className="text-sm sm:text-2xl md:text-3xl lg:text-3xl font-normal text-black dark:text-white pt-8 sm:pt-0 ">
              {t('hero.title')} <br />
              <AnimatedGradientText
                className="text-5xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[6rem] font-bold leading-none rounded-none pb-0 mb-0"
                colors={['#229302', '#40ffaa', '#4040ff']}
                animationSpeed={10}
              >
                {t('homepage.titleHighlight')}
              </AnimatedGradientText>
            </h1>
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl max-w-6xl mx-auto font-medium pt-0">
              {t('homepage.subtitle')}
            </h1>

            <div className="flex flex-wrap items-center gap-3 p-5 sm:gap-4 md:gap-6 justify-center mt-6 sm:mt-8 md:mt-10 lg:mt-12">
              {showSignUp && (
                <Link to="/signup" className="w-full sm:w-auto">
                  <Button
                    size="default"
                    className="w-full sm:w-auto text-xs sm:text-sm md:text-base lg:text-lg px-6 py-3 sm:px-6 sm:py-2 md:px-8 md:py-3 lg:px-10 lg:py-4 font-medium border-2 hover:scale-105 transition-all duration-200 shadow-lg relative overflow-hidden"
                  >
                    <div
                      className="absolute inset-0 w-full h-full opacity-10 bg-noise [mask-image:radial-gradient(#fff,transparent,75%)]"
                      style={{
                        backgroundSize: '30%',
                      }}
                    ></div>
                    <span className="relative z-10">{t('nav.signup')}</span>
                  </Button>
                </Link>
              )}
              <Link to="/book-demo" className="w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="default"
                  className="w-full sm:w-auto text-xs sm:text-sm md:text-base lg:text-lg px-6 py-3 sm:px-6 sm:py-2 md:px-8 md:py-3 lg:px-10 lg:py-4 font-medium flex items-center justify-center gap-1 sm:gap-2 md:gap-3 group border-2 hover:scale-105 transition-all duration-200 shadow-lg relative overflow-hidden"
                >
                  <div
                    className="absolute inset-0 w-full h-full opacity-10 bg-noise [mask-image:radial-gradient(#fff,transparent,75%)]"
                    style={{
                      backgroundImage: 'url(/noise.webp)',
                      backgroundSize: '30%',
                    }}
                  ></div>
                  <span className="relative z-10">{t('nav.bookDemo')}</span>
                  <HiArrowRight className="text-foreground group-hover:translate-x-1 sm:group-hover:translate-x-2 stroke-[1px] h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 transition-transform duration-200 relative z-10" />
                </Button>
              </Link>
            </div>
          </>
        }
      >
        <div
          className={`p-4 border border-neutral-200 bg-neutral-100 dark:bg-neutral-800 dark:border-neutral-700 rounded-[32px] relative ${isMobile ? 'min-h-[650px]' : ''}`}
        >
          <div className="absolute inset-x-0 bottom-0 h-40 w-full bg-gradient-to-b from-transparent via-white to-white dark:via-black/50 dark:to-black scale-[1.1] pointer-events-none" />
          <div
            className={`p-2 bg-white dark:bg-black dark:border-neutral-700 border border-neutral-200 rounded-[24px] ${isMobile ? 'min-h-[600px]' : ''}`}
          >
            <img
              src={dashboardImage}
              alt="AI Platform Dashboard"
              className="w-full h-full rounded-[20px]"
              draggable={false}
              width={1920}
              height={1080}
              style={{
                objectPosition: 'center top',
                height: isMobile ? '600px' : 'auto',
                objectFit: isMobile ? 'contain' : 'cover',
              }}
            />
          </div>
        </div>
      </ContainerScroll>
    </div>
  );
}
